import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Wallet, Plus, Search, Trash2, X, CreditCard, DollarSign } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'

export default function PaymentsPage() {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [form, setForm] = useState({
    customer_name: '',
    amount: '',
    payment_method: 'کارت‌خوان',
    notes: ''
  })

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['/payments', { search }],
    queryFn: () => api.get('/payments', { params: { search } }).then(r => r.data),
  })

  const createMutation = useMutation({
    mutationFn: (newPay: any) => api.post('/payments', {
      ...newPay,
      amount: Number(newPay.amount) || 0
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/payments'] })
      toast.success('پرداخت جدید ثبت شد')
      setIsOpen(false)
      setForm({ customer_name: '', amount: '', payment_method: 'کارت‌خوان', notes: '' })
    },
    onError: () => toast.error('خطا در ثبت پرداخت')
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/payments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/payments'] })
      toast.success('پرداخت حذف شد')
    },
    onError: () => toast.error('خطا در حذف پرداخت')
  })

  const items = data?.items || []

  return (
    <div className="p-4 space-y-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-neutral-900 dark:text-white">مدیریت پرداخت‌ها</h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">ثبت و تراکنش‌های دریافتی و پرداختی مشتریان و تامین‌کنندگان</p>
        </div>
        <Button onClick={() => setIsOpen(true)}>
          <Plus className="w-4 h-4 ml-1" />
          ثبت پرداخت جدید
        </Button>
      </motion.div>

      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input placeholder="جستجوی دریافت/پرداخت..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="card p-4"><Skeleton className="h-5 w-48" /></div>)}</div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center">
          <Wallet className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400">تراکنشی ثبت نشده است</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 dark:bg-neutral-800">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">شماره پیگیری</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">مشتری / طرف حساب</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">مبلغ (تومان)</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">روش پرداخت</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">تاریخ</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">وضعیت</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {items.map((item: any) => (
                  <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                    <td className="px-4 py-3 text-sm font-medium text-neutral-900 dark:text-white">
                      {item.payment_number || 'PAY-001'}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-800 dark:text-neutral-200">{item.customer_name || 'مشتری عمومی'}</td>
                    <td className="px-4 py-3 text-sm font-bold text-emerald-600 dark:text-emerald-400">
                      {Number(item.amount || item.total_amount || 0).toLocaleString()} تومان
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300">
                      <div className="flex items-center gap-1.5">
                        <CreditCard className="w-3.5 h-3.5 text-neutral-400" />
                        {item.payment_method || 'کارت‌خوان'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString('fa-IR') : 'امروز'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Badge variant="success">{item.status || 'موفق'}</Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-left">
                      <button
                        onClick={() => deleteMutation.mutate(item.id)}
                        className="text-red-500 hover:text-red-700 p-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/20"
                        title="حذف"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal create payment */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-neutral-900 rounded-2xl p-6 w-full max-w-md shadow-xl border border-neutral-200 dark:border-neutral-800 space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-neutral-100 dark:border-neutral-800">
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-500" />
                  ثبت دریافت / پرداخت جدید
                </h3>
                <button onClick={() => setIsOpen(false)} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (!form.customer_name || !form.amount) {
                    toast.error('نام طرف حساب و مبلغ الزامی است')
                    return
                  }
                  createMutation.mutate(form)
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">نام طرف حساب / مشتری</label>
                  <Input
                    placeholder="مثلا: رضا کریمی"
                    value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">مبلغ (تومان)</label>
                  <Input
                    type="number"
                    placeholder="1000000"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">روش پرداخت</label>
                  <select
                    value={form.payment_method}
                    onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="کارت‌خوان">کارت‌خوان (POS)</option>
                    <option value="نقدی">نقدی</option>
                    <option value="کارت به کارت">کارت به کارت</option>
                    <option value="چک">چک صیادی</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">توضیحات / بابت</label>
                  <Input
                    placeholder="تسویه فاکتور و..."
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                    انصراف
                  </Button>
                  <Button type="submit" isLoading={createMutation.isPending}>
                    ذخیره پرداخت
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
